import { useTranslation } from 'react-i18next';
import { Card } from '../common/Card';
import { Badge } from '../common/Badge';
import { formatPercent } from '../../utils/formatters';

export function ValueAnalysis({ prediction, market, accessStatus }) {
  const { t } = useTranslation();
  const isAuthRequired = accessStatus?.isAuthRequired;
  const isMembershipRequired = accessStatus?.isMembershipRequired;

  if (!prediction || !market) {
    const emptyState = isAuthRequired
      ? { text: t('auth.loginRequired', '请先登录'), className: 'text-accent' }
      : isMembershipRequired
      ? { text: t('membership.noAccess', '没有会员权限'), className: 'text-accent-warm' }
      : { text: t('detail.noValueData'), className: 'text-gray-500' };
    return (
      <Card>
        <h3 className="font-semibold text-accent mb-4 flex items-center gap-2">
          <span>⚖️</span> {t('detail.valueAnalysis')}
        </h3>
        <div className={`${emptyState.className} text-sm text-center py-8`}>
          {emptyState.text}
        </div>
      </Card>
    );
  }

  const { prob, edge, side } = prediction;
  const { priceA, priceB, teamA, teamB } = market;

  // Calculate model probabilities
  const modelProbA = prob || 0.5;
  const modelProbB = 1 - modelProbA;

  // Market prices (as percentages)
  const marketProbA = priceA || 0.5;
  const marketProbB = priceB || 0.5;

  // Calculate EV for each side
  // EV = (Model Prob - Market Prob) / Market Prob * 100
  const evA = ((modelProbA - marketProbA) / marketProbA * 100);
  const evB = ((modelProbB - marketProbB) / marketProbB * 100);

  // Determine which side is undervalued
  const isAUndervalued = modelProbA > marketProbA;
  const isBUndervalued = modelProbB > marketProbB;
  
  const undervaluedTeam = evB > evA ? teamB : (evA > 0 ? teamA : null);
  const bestEV = Math.max(evA, evB);

  // Get recommendation
  const getRecommendation = () => {
    if (bestEV > 10) return { text: t('detail.strongBuy'), variant: 'positive', icon: '⚡' };
    if (bestEV > 5) return { text: t('detail.buy'), variant: 'positive', icon: '✓' };
    if (bestEV > 0) return { text: t('detail.slightValue'), variant: 'neutral', icon: '→' };
    return { text: t('detail.noValue'), variant: 'negative', icon: '✗' };
  };

  const recommendation = getRecommendation();

  return (
    <Card>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-accent flex items-center gap-2">
          <span>⚖️</span> {t('detail.valueAnalysis')}
        </h3>
      </div>

      {/* Market vs Model Comparison */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-surface-highlight/60 rounded-lg p-4">
          <div className="text-xs text-gray-400 mb-2">{t('detail.marketPricing')}</div>
          <div className="text-2xl font-bold">
            {Math.round(marketProbA * 100)}%
            <span className="text-gray-500 mx-2">/</span>
            {Math.round(marketProbB * 100)}%
          </div>
        </div>
        <div className="bg-surface-highlight/60 rounded-lg p-4">
          <div className="text-xs text-gray-400 mb-2">{t('detail.modelProbability')}</div>
          <div className="text-2xl font-bold text-accent">
            {Math.round(modelProbA * 100)}%
            <span className="text-gray-500 mx-2">/</span>
            {Math.round(modelProbB * 100)}%
          </div>
        </div>
      </div>

      {/* EV Display */}
      <div className="bg-surface-highlight/60 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-400">{t('detail.expectedValue')}</span>
          <span className={`text-3xl font-bold ${bestEV > 0 ? 'text-positive' : 'text-negative'}`}>
            {bestEV > 0 ? '+' : ''}{bestEV.toFixed(1)}%
          </span>
        </div>
      </div>

      {/* Undervalued Team Highlight */}
      {undervaluedTeam && bestEV > 0 && (
        <div className="bg-positive/10 border border-positive/30 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xl">{recommendation.icon}</span>
              <span className="font-bold text-positive">{undervaluedTeam}</span>
              <span className="text-gray-400">{t('detail.undervalued')}</span>
            </div>
            <Badge variant={recommendation.variant}>{recommendation.text}</Badge>
          </div>
        </div>
      )}

      {/* Detailed EV Breakdown */}
      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between p-3 bg-surface-highlight/50 rounded-lg">
          <span className="text-sm">{teamA}</span>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">
              {t('detail.market')}: {Math.round(marketProbA * 100)}%
            </span>
            <span className="text-xs text-gray-400">
              {t('detail.model')}: {Math.round(modelProbA * 100)}%
            </span>
            <span className={`font-bold ${evA > 0 ? 'text-positive' : 'text-negative'}`}>
              EV: {evA > 0 ? '+' : ''}{evA.toFixed(1)}%
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between p-3 bg-surface-highlight/50 rounded-lg">
          <span className="text-sm">{teamB}</span>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">
              {t('detail.market')}: {Math.round(marketProbB * 100)}%
            </span>
            <span className="text-xs text-gray-400">
              {t('detail.model')}: {Math.round(modelProbB * 100)}%
            </span>
            <span className={`font-bold ${evB > 0 ? 'text-positive' : 'text-negative'}`}>
              EV: {evB > 0 ? '+' : ''}{evB.toFixed(1)}%
            </span>
          </div>
        </div>
      </div>

      {/* Recommendation Text */}
      <div className="border-t border-glass-border pt-4">
        <p className="text-sm text-gray-400">
          {bestEV > 5
            ? t('detail.valueRecommendationStrong', { team: undervaluedTeam })
            : bestEV > 0
            ? t('detail.valueRecommendationMild', { team: undervaluedTeam })
            : t('detail.valueRecommendationNone')}
        </p>
      </div>
    </Card>
  );
}
